import React, {
    useContext,
    useState,
    useRef,
    useLayoutEffect,
    useEffect
} from "react";
import {
    Spinner,
    MessageBar,
    MessageBarType,
    IconButton
} from "@fluentui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { DataContext } from "../DataContext";
import { ChatMessage, IAskAiResponse } from "../../services/AiService";
import useMobileDetector from "../useMobileDetector";
import { useParams } from "react-router-dom";
import { OrbUserContext } from "../OrbUserContext";

// interface ChatMessage {
//     role: "user" | "assistant";
//     content: string;
// }

const formatMsgTime = (iso?: string): string => {
    if (!iso) return "";
    const d = new Date(iso);

    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.getFullYear().toString().slice(2);
    const hours = d.getHours() % 12 || 12;
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = d.getHours() >= 12 ? "PM" : "AM";

    return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
};

const AiChat: React.FC = () => {
    const { chatId } = useParams<{ chatId: string }>();
    const decoded = atob(chatId);
    const actualChatId = Number(decoded.split("_")[1]);
    //console.log("AiChat decoded chatId:", decoded, "actualChatId:", actualChatId);

    const { userContext } = useContext(OrbUserContext);
    //console.log("AiChat userContext:", userContext.UserTitle, "UserId:", userContext.UserId);
    //console.log("AiChat chatId:", chatId);
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [error, setError] = useState<string | null>(null);

    const isMobile = useMobileDetector();

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const {
        dataServices: { aiService }
    } = useContext(DataContext);

    useLayoutEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }, [messages, loading]);

    useEffect(() => {
        if (!actualChatId) return;

        const loadMessages = async () => {
            try {
                const history = await aiService.getMessages(Number(actualChatId));
                setMessages(history);
            } catch {
                // history load failure is non-fatal, start fresh
            }
        };

        loadMessages();
    }, [actualChatId]);

    const adjustHeight = (el: HTMLTextAreaElement) => {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPrompt(e.target.value);
        adjustHeight(e.target);
    };

    const sendMessage = async () => {
        if (!prompt.trim() || loading) return;

        const userPrompt = prompt;

        // user message
        setMessages(prev => [
            ...prev,
            {
                role: "user",
                content: userPrompt,
                createdOn: new Date().toISOString(),
                userName: userContext.UserTitle    // ← add
            }
        ]);

        setPrompt("");
        setLoading(true);
        setError(null);

        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }

        try {
            //send chat id with prompt
            const data: IAskAiResponse = await aiService.ask(userPrompt, Number(actualChatId));

            if (data.summarised && data.summary) {
                // Replace entire message history with summary + new exchange
                setMessages([
                    {
                        role: "summary",
                        content: data.summary,
                        createdOn: new Date().toISOString()
                    },
                    {
                        role: "user",
                        content: userPrompt,
                        createdOn: new Date().toISOString(),
                        userName: userContext.UserTitle
                    },
                    {
                        role: "assistant",
                        content: data.response,
                        createdOn: new Date().toISOString()
                    }
                ]);
            } else {
                setMessages(prev => [
                    ...prev,
                    {
                        role: "assistant",
                        content: data.response,
                        createdOn: new Date().toISOString()
                    }
                ]);
            }

        } catch (err: any) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    // const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    //     console.log("Key down", e.key, "Ctrl:", e.ctrlKey);
    //     if (e.key === "Enter") {
    //         if (e.ctrlKey) {
    //             // Ctrl+Enter: insert a newline at cursor position manually
    //             e.preventDefault();
    //             const ta = e.currentTarget;
    //             const start = ta.selectionStart;
    //             const end = ta.selectionEnd;
    //             const newValue = prompt.substring(0, start) + "\n" + prompt.substring(end);
    //             setPrompt(newValue);
    //             // Move cursor after the inserted newline
    //             requestAnimationFrame(() => {
    //                 ta.selectionStart = start + 1;
    //                 ta.selectionEnd = start + 1;
    //                 adjustHeight(ta);
    //             });
    //         } else {
    //             // Plain Enter: send
    //             e.preventDefault();
    //             sendMessage();
    //         }
    //     }
    // };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // On mobile do NOT intercept Enter at all
        // Let keyboard naturally insert newline
        if (isMobile) {
            return;
        }

        // Desktop behavior
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }

        // Shift+Enter naturally inserts newline
    };

    return (
        <div className="aiChatContainer">

            <div className="aiChatMessages" ref={messagesContainerRef} style={{ scrollbarColor: '#f5f5f5 transparent' }}>

                <div className="aiChatHeader">
                    Ask Cloe
                </div>

                {messages.map((msg, index) => (
                    <div key={index} className={`chatBubbleWrapper ${msg.role}`}>

                        {/*msg.role !== "summary" && */(
                            <div className="chatBubbleMeta">
                                {msg.role === "user"
                                    ? `${msg.userName ?? userContext.UserTitle}  ·  ${formatMsgTime(msg.createdOn)}`
                                    : formatMsgTime(msg.createdOn)
                                }
                            </div>
                        )}

                        <div className={`chatBubble ${msg.role}`}>
                            {msg.role === "summary" ? (
                                <>
                                    <div className="summaryLabel">Earlier conversation summarised</div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {msg.content}
                                    </ReactMarkdown>
                                </>
                            ) : msg.role === "user" ? (
                                msg.content
                            ) : (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {msg.content}
                                </ReactMarkdown>
                            )}
                        </div>

                    </div>
                ))}

                {loading && (
                    <div className="chatBubbleWrapper assistant">
                        <div className="chatBubble assistant">
                            <Spinner label="Thinking..." />
                        </div>
                    </div>
                )}

                <div ref={bottomRef} />

            </div>

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {error}
                </MessageBar>
            )}

            <div className="aiChatFooter">

                <div className="aiChatInputRow">

                    <div className="aiChatInput">
                        <textarea
                            ref={textareaRef}
                            className="aiChatTextarea"
                            value={prompt}
                            onChange={handleTextareaChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask Cloe"
                            rows={1}
                        />
                    </div>

                    <IconButton
                        iconProps={{ iconName: "Up" }}
                        onClick={sendMessage}
                        disabled={loading}
                        className="sendButton"
                    />

                </div>

                <div className="aiChatDisclaimer">
                    Cloe is AI and can make mistakes.
                </div>

            </div>

        </div>
    );
};

export default AiChat;